import Alert from '@mui/material/Alert';
import { utils } from 'ethers';
import React, { useState, useEffect, useContext } from 'react';
import { observer } from 'mobx-react';
import { useAccount } from 'wagmi';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { tokenDecimals } from '../../../helpers/config/contracts.config';
import { hardcodedErrorCodes } from '../../../helpers/error-message.helper';
import { balanceStore } from '../../../stores/BalanceStore';
import AmountInput from '../../AmountInput/AmountInput';
import NereusButton from '../../NereusButton/NereusButton';
import StatusIcon from '../../StatusIcon/StatusIcon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';

import './WithdrawDialog.scss';

const buttonTexts = {
    withdraw: 'Confirm Withdraw',
    withdrawing: 'Withdrawing USDT',
    enterAmount: 'Enter Amount',
    insufficientFunds: 'Insufficient Funds'
};

const WithdrawDialog: React.FC = observer(() => {
    const { address } = useAccount();
    const [amount, setAmount] = useState<string>('0');
    const [isPending, setIsPending] = useState<boolean>(false);
    const { depositWithdrawService } = useContext(ServicesContext);

    let error = null
    if (amount && Number(amount) > Number(balanceStore.usdtAvailableBalanceDecimal)) {
        error = hardcodedErrorCodes.FE_NOT_ENOUGH_WITHDRAWABLE_BALANCE;
    }

    const btnDisabled = !Number(amount) || !!error
    const show24HDescription = !btnDisabled && !isPending && !error

    let buttonText = buttonTexts.withdraw
    if (!amount || Number(amount) === 0) {
        buttonText = buttonTexts.enterAmount
    } else if (error === hardcodedErrorCodes.FE_NOT_ENOUGH_WITHDRAWABLE_BALANCE) {
        buttonText = buttonTexts.insufficientFunds;
    }
    if (isPending) {
        buttonText = buttonTexts.withdrawing
    }

    useEffect(() => {
        if (address) {
            balanceStore.fetchBalance(address);
        }
    }, [address]);

    const handleWithdraw = async () => {
        if (!amount) {
            return;
        }

        setIsPending(true);
        try {
            await depositWithdrawService.withdraw(utils.parseUnits(amount, tokenDecimals.usdt));
        } catch {}
        setIsPending(false);
    }

    return (<ModalWrapper className="WithdrawDialog"
                          headerChildren={<h2 className="nereus-body-m">Withdraw</h2>}
                          bodyChildren={<div className="WithdrawDialog__body">
                              <AmountInput label="Amount" asset="USDT"
                                           hasMaxButton={true}
                                           value={amount}
                                           available={balanceStore.usdtAvailableBalanceDecimal}
                                           onChange={amount => setAmount(amount === '' ? '0' : amount + '')}/>
                              {error &&
                                  <Alert className="WithdrawDialog__error mt-4" variant="standard" severity="error">
                                      <span className="nereus-caption">{error}</span>
                                  </Alert>}

                              {show24HDescription &&
                                  <Alert className="WithdrawDialog__error mt-4" variant="standard" severity="info">
                                      <span className="nereus-caption">Before you proceed, please note that withdrawal can take <span className="nereus-color-main">up to 24 hours</span>. Your
funds are safely being transferred through
the blockchain system.</span>
                                  </Alert>}
                              <div className="flex pb-4 mt-4">
                                  <NereusButton disabled={btnDisabled || isPending} variant="contained"
                                                onClick={handleWithdraw}
                                                className="bg-light-green text-dark-blue w-full">
                                      {buttonText} {isPending && <StatusIcon className="ml-1 inline" status="disabled-pending" />}
                                  </NereusButton>
                              </div>
                          </div>}
    />);
});

export default WithdrawDialog;
